import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { ColumnsType } from "antd/lib/table";
import moment from "moment";
import { Badge, Button, Col, Modal, Row, Space, Tooltip } from "antd";
import {
  ArrowLeftOutlined,
  BankOutlined,
  BranchesOutlined,
  CalendarOutlined,
  DeleteOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  FileSearchOutlined,
  HomeOutlined,
  HourglassOutlined,
  MailOutlined,
  PhoneOutlined,
  PlusCircleOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { parseISO } from "date-fns";
import axios, { AxiosError } from "axios";
import { Container, Header } from "./styles";
import { useToast } from "../../hooks/toast";
import api from "../../services/api";
import UserRoles from "../../enums/UserRoles";
import Stat from "../../components/Stat";
import EditFieldModal from "../../components/UserActions/EditFieldModal";
import FieldValueHistoryModal from "./FieldValueHistoryModal";
import Table from "../../components/Table";
import { ISkill } from "../Users";
import EquipmentModal from "./EquipmentModal";
import SkillModal from "./SkillModal";
import UserAvatar from "../../components/UserAvatar";
import { useAuth } from "../../hooks/auth";

const { confirm } = Modal;

interface IParams {
  userId: string;
}

export interface IUser {
  id: string;
  name: string;
  email: string;
  phone_number: string | null;
  avatar: string | null;
  avatar_url: string | null;
  hiring_model: "CORPORATE" | "SHAREHOLDER" | "CLT" | "CLT_TEMP";
  role: UserRoles;
  seniority: "JUNIOR" | "FULL" | "SENIOR";
  monthly_productive_hours: number | null;
  status: "ACTIVE" | "INACTIVE";
  base_salary: number | null;
  medical_insurance_value: number | null;
  bonus_allocation: number | null;
  cost_operation: number | null;
  thirteenth_salary: number | null;
  paid_time_off: number | null;
  paid_clt: number | null;
  flag_equipment: number | null;
  user_equipment_id: string | null;
  manager_id: string | null;
  admission_date: string;
  dismissal_date: string;
  consultants_histories: Ihistory[];
  equipament?: IEquipment[];
  skills: ISkill[];
  value_allocation?: number;
  model?: "ALLOCATION" | "FACTORY";
}

export interface IEquipment {
  id: string;
  equipment_id: string;
  equipment_type: "MONITOR" | "NOTEBOOK" | "MEMORIA" | "SSD" | "MOBILIARIO" | "TECLADO" | "MOUSE";
  date_acquisition: Date;
  status: "IN USE" | "FREE" | "DEFECT";
  description_defect: string | null;
  assignment: string | null;
  equipment_description: string;
}

export interface Ihistory {
  id: string;
  create_at: string;
  m_base_salary: number | null;
  m_bonus_allocation: number | null;
  m_cost_operation: number | null;
  m_medical_insurance_value: number | null;
  m_paid_clt: number | null;
  m_paid_time_off: number | null;
  m_thirteenth_salary: number | null;
  modificatory_id: string | null;
  modify_id: string | null;
}

const Project: React.FC = () => {
  const { userId } = useParams<IParams>();
  const { addToast } = useToast();
  const { t } = useTranslation(["consultants"]);
  const [user, setUser] = useState<IUser | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalVisibleSkill, setModalVisibleSkill] = useState(false);
  const [loadingDeleteEquipment, setLoadingDeleteEquipment] = useState(false);
  const history = useHistory();

  const { user: authUser } = useAuth();

  const handleDeleteEquipament = (equipmentId: string) => {
    setLoadingDeleteEquipment(true);
    let flag_equipment = 1;
    if (user?.equipament && user?.equipament?.length - 1 === 0)
      flag_equipment = 0;
    api
      .patch(`/equipments/${equipmentId}`, {
        status: "FREE",
        user_id: user?.id,
        flag_equipment,
      })
      .then(() => {
        setUser(oldUser => {
          if (oldUser && oldUser?.equipament) {
            return {
              ...oldUser,
              equipament: [
                ...oldUser.equipament?.filter(
                  equip => equip.id !== equipmentId,
                ),
              ],
            };
          }
          return user;
        });
        addToast({
          type: "success",
          title: "Equipamento removido",
          description: "O equipamento foi removido com sucesso",
        });
      })
      .catch(err => {
        if (axios.isAxiosError(err)) {
          const serverError = err as AxiosError;
          console.log(serverError);
        }
      })
      .finally(() => setLoadingDeleteEquipment(false));
  };

  useEffect(() => {
    setLoading(true);
    api
      .get<IUser>(`/users/${userId}`)
      .then(response => {
        setUser(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os consultores, tente novamente.",
        });
      })
      .finally(() => setLoading(false));
  }, [addToast, userId]);

  const showDelConfirm = useCallback(
    (id, name) => {
      confirm({
        title: t("deleteModalEquipment.title"),
        icon: <ExclamationCircleOutlined />,
        content: t("deleteModalEquipment.alert", { name }),
        centered: true,
        cancelText: t("deleteModalEquipment.no"),
        okButtonProps: {
          type: "primary",
          danger: true,
          loading: loadingDeleteEquipment,
        },
        onOk: () => handleDeleteEquipament(id),
      });
    },
    [t, handleDeleteEquipament],
  );

  const roleModelOption = useMemo(() => {
    return [
      { label: t("role.DEVELOPER"), value: "DEVELOPER" },
      { label: t("role.TESTER"), value: "TESTER" },
      { label: t("role.MANAGER"), value: "MANAGER" },
      { label: t("role.REQUIREMENTS_ANALYST"), value: "REQUIREMENTS_ANALYST" },
      { label: t("role.ADMINISTRATIVE"), value: "ADMINISTRATIVE" },
    ];
  }, [t]);

  const hiringModelOption = useMemo(() => {
    return [
      {
        label: t("hiring_model.CORPORATE"),
        value: "CORPORATE",
      },
      {
        label: t("hiring_model.SHAREHOLDER"),
        value: "SHAREHOLDER",
      },
      {
        label: t("hiring_model.CLT"),
        value: "CLT",
      },
      {
        label: t("hiring_model.CLT_TEMP"),
        value: "CLT_TEMP",
      },
    ];
  }, [t]);
  const modelOption = useMemo(() => {
    return [
      {
        label: t("model_name.FACTORY"),
        value: "FACTORY",
      },
      {
        label: t("model_name.ALLOCATION"),
        value: "ALLOCATION",
      },
    ];
  }, [t]);

  const extraActionSkill = useMemo(() => {
    return (
      <Tooltip title={t("modalAddSkills.add")}>
        <Button
          icon={<PlusCircleOutlined />}
          type="link"
          size="large"
          onClick={() => setModalVisibleSkill(true)}
        />
      </Tooltip>
    );
  }, [t]);

  const extraAction = useMemo(() => {
    return (
      <Tooltip title={t("modalAddEquipment.add")}>
        <Button
          icon={<PlusCircleOutlined />}
          type="link"
          size="large"
          onClick={() => setModalVisible(true)}
        />
      </Tooltip>
    );
  }, [t]);

  const senioridadeOption = useMemo(() => {
    return [
      {
        label: "JUNIOR",
        value: "JUNIOR",
      },
      {
        label: t("FULL"),
        value: "FULL",
      },
      {
        label: "SENIOR",
        value: "SENIOR",
      },
    ];
  }, []);

  const skillsColumns: ColumnsType<ISkill> = [
    {
      title: t("skills.name"),
      dataIndex: "name",
      render: (name: string) => {
        return name || t("nothing.infomation");
      },
    },
  ];

  const equipamentColumns: ColumnsType<IEquipment> = [
    {
      title: t("equipments.equipment_id"),
      dataIndex: "equipment_id",
      render: (equipment_id: string) => {
        return equipment_id || t("nothing.infomation");
      },
    },
    {
      title: t("equipments.equipment_type"),
      dataIndex: "equipment_type",
      render: (equipment_type: string) => {
        return equipment_type || t("nothing.infomation");
      },
    },
    {
      title: t("equipments.equipment_description"),
      dataIndex: "equipment_description",
      render: (equipment_description: string) => {
        return equipment_description || t("nothing.infomation");
      },
    },
    {
      title: t("equipments.status"),
      dataIndex: "status",
      render: (status: string) => {
        return status || t("nothing.infomation");
      },
    },
    {
      title: t("equipments.assignment"),
      dataIndex: "assignment",
      render: (assignment: string) => {
        return assignment || t("nothing.infomation");
      },
    },
    {
      title: t("equipments.equipment_acquisition"),
      dataIndex: "date_acquisition",
      render: date =>
        date &&
        `${parseISO(date).toLocaleDateString("pt-BR")} às ${parseISO(
          date,
        ).toLocaleTimeString("pt-BR")}`,
    },
    {
      title: t("action"),
      dataIndex: "actions",
      fixed: "right",
      render: (_, record) => (
        <>
          <Button
            icon={<DeleteOutlined />}
            type="link"
            danger
            onClick={() => showDelConfirm(record.id, record.equipment_type)}
          />
        </>
      ),
    },
  ];

  const handleCloseModal = useCallback((useredit?: IUser) => {
    if (useredit) {
      setUser(useredit);
    }
    setModalVisible(false);
  }, []);

  const handleCloseModalSkill = useCallback((useredit?: IUser) => {
    if (useredit) {
      setUser(useredit);
    }
    setModalVisibleSkill(false);
  }, []);

  const managerValidation = useMemo(() => {
    return authUser?.role === UserRoles.Manager && user;
  }, [authUser, user]);

  const formatPhoneNumber = useCallback(() => {
    const phoneNumberString = user?.phone_number || "";
    const cleaned = `${phoneNumberString}`.replace(/\D/g, "");
    const match = cleaned.match(/^(\d{2})(\d{5})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phoneNumberString;
  }, [user?.phone_number]);

  return (
    <Container>
      <EquipmentModal
        visible={modalVisible}
        handleCloseModal={handleCloseModal}
        user={user}
      />
      <SkillModal
        visible={modalVisibleSkill}
        handleCloseModal={handleCloseModalSkill}
        user={user}
      />
      <Header>
        <Button
          onClick={() => history.goBack()}
          type="link"
          icon={<ArrowLeftOutlined />}
          size="large"
        >
          Voltar
        </Button>
      </Header>
      <Space direction="vertical" size="middle" style={{ display: "flex" }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} lg={8}>
            <Space direction="vertical">
              <Stat
                title={t("name")}
                value={`${user?.name}`}
                icon={
                  user ? (
                    <UserAvatar
                      name={user?.name}
                      url={user?.avatar_url || ""}
                      size={36}
                    />
                  ) : (
                    <></>
                  )
                }
                loading={loading}
                editComponent={
                  managerValidation ? (
                    <EditFieldModal
                      fieldName="name"
                      userUpdate={user as IUser}
                      handleUpdateProject={proj => setUser(proj)}
                      inputType="INPUT"
                      title={t("name")}
                    />
                  ) : (
                    <></>
                  )
                }
                valueHistoryComponent={
                  user ? (
                    <FieldValueHistoryModal
                      userUpdate={user as IUser}
                      fieldName="m_name"
                      title={t("name")}
                    />
                  ) : (
                    <></>
                  )
                }
              />
              <Stat
                title={t("seniority")}
                value={t(`${user?.seniority}`)}
                icon={<UserOutlined />}
                loading={loading}
                editComponent={
                  managerValidation ? (
                    <EditFieldModal
                      fieldName="seniority"
                      userUpdate={user as IUser}
                      handleUpdateProject={proj => setUser(proj)}
                      inputType="SELECT"
                      selectOptions={senioridadeOption}
                      title={t("seniority")}
                    />
                  ) : (
                    <></>
                  )
                }
                valueHistoryComponent={
                  user ? (
                    <FieldValueHistoryModal
                      userUpdate={user as IUser}
                      fieldName="m_seniority"
                      title={t("seniority")}
                    />
                  ) : (
                    <></>
                  )
                }
              />
              <Stat
                title={t("admission_date")}
                value={`${moment(user?.admission_date).format(
                  "MMMM [de] YYYY",
                )}`}
                icon={<CalendarOutlined />}
                loading={loading}
                editComponent={
                  managerValidation ? (
                    <EditFieldModal
                      fieldName="admission_date"
                      userUpdate={user as IUser}
                      handleUpdateProject={proj => setUser(proj)}
                      inputType="DATE"
                      title={t("admission_date")}
                    />
                  ) : (
                    <></>
                  )
                }
                valueHistoryComponent={
                  user ? (
                    <FieldValueHistoryModal
                      userUpdate={user as IUser}
                      fieldName="m_admission_date"
                      title={t("admission_date")}
                    />
                  ) : (
                    <></>
                  )
                }
              />
              <Stat
                title={t("role.title")}
                value={t(`role.${user?.role}`)}
                icon={<BranchesOutlined />}
                loading={loading}
                editComponent={
                  managerValidation ? (
                    <EditFieldModal
                      fieldName="role"
                      userUpdate={user as IUser}
                      handleUpdateProject={proj => setUser(proj)}
                      inputType="SELECT"
                      selectOptions={roleModelOption}
                      title={t("role.title")}
                    />
                  ) : (
                    <></>
                  )
                }
                valueHistoryComponent={
                  user ? (
                    <FieldValueHistoryModal
                      userUpdate={user as IUser}
                      fieldName="m_role"
                      title={t("role.title")}
                    />
                  ) : (
                    <></>
                  )
                }
              />
              {managerValidation && (
                <>
                  <Stat
                    title={t("thirteenth_salary")}
                    value={`${user?.thirteenth_salary ?? 0}`}
                    icon={
                      <BankOutlined
                        style={{ color: "#e95011", fontSize: "20px" }}
                      />
                    }
                    loading={loading}
                    editComponent={<></>}
                    valueHistoryComponent={
                      user ? (
                        <FieldValueHistoryModal
                          userUpdate={user as IUser}
                          fieldName="m_thirteenth_salary"
                          title={t("thirteenth_salary")}
                        />
                      ) : (
                        <></>
                      )
                    }
                  />
                  <Stat
                    title={t("bonus_allocation")}
                    value={`${user?.bonus_allocation ?? 0}`}
                    icon={
                      <BankOutlined
                        style={{ color: "#e95011", fontSize: "20px" }}
                      />
                    }
                    loading={loading}
                    editComponent={
                      user ? (
                        <EditFieldModal
                          fieldName="bonus_allocation"
                          userUpdate={user as IUser}
                          handleUpdateProject={proj => setUser(proj)}
                          inputType="MASKBRL"
                          title={t("bonus_allocation")}
                        />
                      ) : (
                        <></>
                      )
                    }
                    valueHistoryComponent={
                      user ? (
                        <FieldValueHistoryModal
                          userUpdate={user as IUser}
                          fieldName="m_bonus_allocation"
                          title={t("bonus_allocation")}
                        />
                      ) : (
                        <></>
                      )
                    }
                  />
                  {(user?.hiring_model === "CLT" ||
                    user?.hiring_model === "CLT_TEMP") && (
                    <Stat
                      title={t("paid_clt")}
                      value={t(`${user?.paid_clt ?? 0}`)}
                      noStyleIcon
                      loading={loading}
                      icon={
                        <BankOutlined
                          style={{ color: "#e95011", fontSize: "20px" }}
                        />
                      }
                      valueHistoryComponent={
                        user ? (
                          <FieldValueHistoryModal
                            userUpdate={user as IUser}
                            fieldName="m_paid_clt"
                            title={t("paid_clt")}
                          />
                        ) : (
                          <></>
                        )
                      }
                    />
                  )}
                </>
              )}
            </Space>
          </Col>
          <Col xs={24} lg={8}>
            <Space direction="vertical">
              <Stat
                title={t("phone_number")}
                value={formatPhoneNumber()}
                noFormat
                icon={<PhoneOutlined />}
                loading={loading}
                editComponent={
                  managerValidation ? (
                    <EditFieldModal
                      fieldName="phone_number"
                      userUpdate={user as IUser}
                      handleUpdateProject={proj => setUser(proj)}
                      inputType="INPUT"
                      title={t("phone_number")}
                    />
                  ) : (
                    <></>
                  )
                }
                valueHistoryComponent={
                  user ? (
                    <FieldValueHistoryModal
                      userUpdate={user as IUser}
                      fieldName="m_phone_number"
                      title={t("phone_number")}
                    />
                  ) : (
                    <></>
                  )
                }
              />
              <Stat
                title={t("email")}
                value={user?.email || ""}
                icon={<MailOutlined />}
                loading={loading}
                editComponent={
                  managerValidation ? (
                    <EditFieldModal
                      fieldName="email"
                      userUpdate={user as IUser}
                      handleUpdateProject={proj => setUser(proj)}
                      inputType="INPUT"
                      title={t("email")}
                    />
                  ) : (
                    <></>
                  )
                }
                valueHistoryComponent={
                  user ? (
                    <FieldValueHistoryModal
                      userUpdate={user as IUser}
                      fieldName="m_email"
                      title={t("email")}
                    />
                  ) : (
                    <></>
                  )
                }
              />
              <Stat
                title={t("dismissal_date")}
                value={`${moment(user?.dismissal_date).format(
                  "MMMM [de] YYYY",
                )}`}
                icon={<CalendarOutlined />}
                loading={loading}
                editComponent={
                  managerValidation ? (
                    <EditFieldModal
                      fieldName="dismissal_date"
                      userUpdate={user as IUser}
                      handleUpdateProject={proj => setUser(proj)}
                      inputType="DATE"
                      title={t("dismissal_date")}
                    />
                  ) : (
                    <></>
                  )
                }
                valueHistoryComponent={
                  user ? (
                    <FieldValueHistoryModal
                      userUpdate={user as IUser}
                      fieldName="m_dismissal_date"
                      title={t("dismissal_date")}
                    />
                  ) : (
                    <></>
                  )
                }
              />
              {managerValidation && (
                <>
                  <Stat
                    title={t("base_salary")}
                    value={`${user?.base_salary ?? 0}`}
                    icon={
                      <BankOutlined
                        style={{ color: "#e95011", fontSize: "20px" }}
                      />
                    }
                    loading={loading}
                    editComponent={
                      user ? (
                        <EditFieldModal
                          fieldName="base_salary"
                          userUpdate={user as IUser}
                          handleUpdateProject={proj => setUser(proj)}
                          inputType="MASKBRL"
                          title={t("base_salary")}
                          exist_thirteenth_salary={!user?.thirteenth_salary}
                        />
                      ) : (
                        <></>
                      )
                    }
                    valueHistoryComponent={
                      user ? (
                        <FieldValueHistoryModal
                          userUpdate={user as IUser}
                          fieldName="m_base_salary"
                          title={t("base_salary")}
                        />
                      ) : (
                        <></>
                      )
                    }
                  />
                  <Stat
                    title={t("medical_insurance_value")}
                    value={`${user?.medical_insurance_value ?? 0}`}
                    icon={
                      <BankOutlined
                        style={{ color: "#e95011", fontSize: "20px" }}
                      />
                    }
                    loading={loading}
                    editComponent={
                      user ? (
                        <EditFieldModal
                          fieldName="medical_insurance_value"
                          userUpdate={user as IUser}
                          handleUpdateProject={proj => setUser(proj)}
                          inputType="MASKBRL"
                          title={t("medical_insurance_value")}
                        />
                      ) : (
                        <></>
                      )
                    }
                    valueHistoryComponent={
                      user ? (
                        <FieldValueHistoryModal
                          userUpdate={user as IUser}
                          fieldName="m_medical_insurance_value"
                          title={t("medical_insurance_value")}
                        />
                      ) : (
                        <></>
                      )
                    }
                  />
                  <Stat
                    title={t("paid_time_off")}
                    value={`${user?.paid_time_off ?? 0}`}
                    icon={
                      <BankOutlined
                        style={{ color: "#e95011", fontSize: "20px" }}
                      />
                    }
                    loading={loading}
                    editComponent={
                      user ? (
                        <EditFieldModal
                          fieldName="paid_time_off"
                          userUpdate={user as IUser}
                          handleUpdateProject={proj => setUser(proj)}
                          inputType="MASKBRL"
                          title={t("paid_time_off")}
                        />
                      ) : (
                        <></>
                      )
                    }
                    valueHistoryComponent={
                      user ? (
                        <FieldValueHistoryModal
                          userUpdate={user as IUser}
                          fieldName="m_paid_time_off"
                          title={t("paid_time_off")}
                        />
                      ) : (
                        <></>
                      )
                    }
                  />
                </>
              )}
            </Space>
          </Col>
          <Col xs={24} lg={8}>
            <Space direction="vertical">
              <Stat
                title={t("status")}
                value={t(`${user?.status}`)}
                noStyleIcon
                loading={loading}
                icon={
                  <Badge
                    text={<></>}
                    color={`${user?.status === "ACTIVE" ? "green" : "red"}`}
                  />
                }
                valueHistoryComponent={
                  user ? (
                    <FieldValueHistoryModal
                      userUpdate={user as IUser}
                      fieldName="m_status"
                      title={t("status")}
                    />
                  ) : (
                    <></>
                  )
                }
              />
              <Stat
                title={t("monthly_productive_hours")}
                value={`${user?.monthly_productive_hours}`}
                icon={<HourglassOutlined />}
                loading={loading}
                editComponent={
                  managerValidation ? (
                    <EditFieldModal
                      fieldName="monthly_productive_hours"
                      userUpdate={user as IUser}
                      handleUpdateProject={proj => setUser(proj)}
                      inputType="INPUT"
                      title={t("monthly_productive_hours")}
                    />
                  ) : (
                    <></>
                  )
                }
                valueHistoryComponent={
                  user ? (
                    <FieldValueHistoryModal
                      userUpdate={user as IUser}
                      fieldName="m_monthly_productive_hours"
                      title={t("monthly_productive_hours")}
                    />
                  ) : (
                    <></>
                  )
                }
              />
              <Stat
                title={t("hiring_model.title")}
                value={t(`hiring_model.${user?.hiring_model}`)}
                icon={<FileSearchOutlined />}
                loading={loading}
                editComponent={
                  managerValidation ? (
                    <EditFieldModal
                      fieldName="hiring_model"
                      userUpdate={user as IUser}
                      handleUpdateProject={proj => setUser(proj)}
                      inputType="SELECT"
                      selectOptions={hiringModelOption}
                      title={t("hiring_model.title")}
                    />
                  ) : (
                    <></>
                  )
                }
                valueHistoryComponent={
                  user ? (
                    <FieldValueHistoryModal
                      userUpdate={user as IUser}
                      fieldName="m_hiring_model"
                      title={t("hiring_model.title")}
                    />
                  ) : (
                    <></>
                  )
                }
              />
              {managerValidation && (
                <>
                  <Stat
                    title={t("cost_operation")}
                    value={`${user?.cost_operation ?? 0}`}
                    noStyleIcon
                    loading={loading}
                    icon={
                      <BankOutlined
                        style={{ color: "#e95011", fontSize: "20px" }}
                      />
                    }
                    valueHistoryComponent={
                      user ? (
                        <FieldValueHistoryModal
                          userUpdate={user as IUser}
                          fieldName="m_cost_operation"
                          title={t("cost_operation")}
                        />
                      ) : (
                        <></>
                      )
                    }
                  />
                  <Stat
                    title={t("value_allocation")}
                    value={t(`${user?.value_allocation ?? 0}`)}
                    noStyleIcon
                    loading={loading}
                    icon={
                      <DollarOutlined
                        style={{ color: "#e95011", fontSize: "20px" }}
                      />
                    }
                    editComponent={
                      user ? (
                        <EditFieldModal
                          fieldName="value_allocation"
                          userUpdate={user as IUser}
                          handleUpdateProject={proj => setUser(proj)}
                          inputType="MASKBRL"
                          title={t("value_allocation")}
                        />
                      ) : (
                        <></>
                      )
                    }
                    valueHistoryComponent={
                      user ? (
                        <FieldValueHistoryModal
                          userUpdate={user as IUser}
                          fieldName="m_value_allocation"
                          title={t("value_allocation")}
                        />
                      ) : (
                        <></>
                      )
                    }
                  />
                  <Stat
                    title={t("model")}
                    value={
                      user?.model
                        ? t(`model_name.${user?.model}`)
                        : "Não Declarado"
                    }
                    noStyleIcon
                    loading={loading}
                    icon={
                      <HomeOutlined
                        style={{ color: "#e95011", fontSize: "20px" }}
                      />
                    }
                    editComponent={
                      user ? (
                        <EditFieldModal
                          fieldName="model"
                          userUpdate={user as IUser}
                          handleUpdateProject={proj => setUser(proj)}
                          inputType="SELECT"
                          selectOptions={modelOption}
                          title={t("model")}
                        />
                      ) : (
                        <></>
                      )
                    }
                  />
                </>
              )}
            </Space>
          </Col>
        </Row>
      </Space>
      <Space
        direction="vertical"
        size="middle"
        style={{ display: "flex", marginTop: "20px" }}
      >
        <Table
          data={user?.equipament || []}
          columns={equipamentColumns}
          title={
            <>
              {t("equipments.title")}
              {/* <Filters
              showFilters={showFilters}
              setShowFilters={handleShowFilters}
            /> */}
            </>
          }
          footer={() => <></>}
          extraActions={extraAction}
          loading={loading}
          pagination={false}
        />
        <Table
          data={user?.skills || []}
          columns={skillsColumns}
          title={
            <>
              {t("skills.title")}
              {/* <Filters
              showFilters={showFilters}
              setShowFilters={handleShowFilters}
            /> */}
            </>
          }
          extraActions={extraActionSkill}
          footer={() => <></>}
          loading={loading}
          pagination={false}
        />
      </Space>
    </Container>
  );
};

export default Project;
